import React, { useState } from "react";
import { toast } from "sonner";
import { Bug, Loader2, Sparkles } from "lucide-react";
import { reApi, errMsg } from "@/lib/residentApi";
import { AskAI } from "@/components/nds/AskAI";
import { ClearMistake } from "@/components/nds/ClearMistake";

const chip = "inline-flex items-center gap-1 px-2 py-1 border rounded-sm font-mono text-[9px] uppercase tracking-[0.14em]";

/** "Fix with me" (AI steps for this item, from its real records, plus follow-up questions) and "Is this a glitch?" (real issue vs system glitch, with proof). */
export default function SessionHelp({ cid, area, residentId, onChanged }) {
  const [help, setHelp] = useState(null);
  const [diag, setDiag] = useState(null);
  const [busy, setBusy] = useState(null);
  const [history, setHistory] = useState([]);
  const run = async (kind) => {
    setBusy(kind);
    try {
      const r = await reApi.post(`/checkins/${cid}/areas/${area.key}/${kind}`);
      if (kind === "help") setHelp(r); else { setDiag(r); if (r.verdict === "glitch") toast.warning("Logged as a possible system glitch in the Problems Inbox"); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <div className="space-y-2" data-testid={`ss-help-${area.key}`}>
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={!!busy} onClick={() => run("help")} data-testid={`ss-fixwithme-${area.key}`} className={`${chip} border-sky-400/50 text-sky-200 hover:bg-sky-400/10`}>
          {busy === "help" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Fix with me</button>
        <button type="button" disabled={!!busy} onClick={() => run("diagnose")} data-testid={`ss-glitch-${area.key}`} className={`${chip} border-white/20 text-nex-muted hover:text-nex-silver`}>
          {busy === "diagnose" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bug className="h-3 w-3" />} Is this a glitch?</button>
        {!area.ok && residentId && <ClearMistake residentId={residentId} alertKey={area.key} onCleared={onChanged} testid={`ss-clear-${area.key}`} />}
      </div>
      {diag && <div data-testid={`ss-glitch-result-${area.key}`} className={`p-3 rounded-sm border text-sm space-y-1 ${diag.verdict === "glitch" ? "border-amber-400/50 bg-amber-400/5" : "border-emerald-400/40 bg-emerald-400/5"}`}>
        <div className={diag.verdict === "glitch" ? "text-amber-200 font-medium" : "text-emerald-300 font-medium"}>
          {diag.verdict === "glitch" ? "Looks like a system glitch — don't change the resident's records for this. It's been logged for a fix." : diag.complete ? "Already complete — nothing to fix here." : "Real issue — the records agree. Fix it or note why not."}</div>
        <p className="text-xs text-nex-silver">{diag.reason}</p>
        <ul className="text-[11px] text-nex-muted list-disc pl-4">{diag.evidence.map((e, i) => <li key={i}>{e}</li>)}</ul>
      </div>}
      {help && <div data-testid={`ss-fixwithme-result-${area.key}`} className="p-3 rounded-sm border border-sky-400/30 bg-sky-400/5 space-y-2">
        <ol className="text-sm text-nex-silver list-decimal pl-5 space-y-1">{help.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
        {help.say && <p className="text-xs text-sky-200">Say to the resident: “{help.say}”</p>}
        <AskAI code={`session_${area.key}`} title={`${area.label}: ${area.text}`} details={JSON.stringify(help.evidence)} step="Sit-down session" history={history} setHistory={setHistory} testid={`ss-ask-${area.key}`} />
      </div>}
    </div>
  );
}
